import { isConsentGranted, subscribeConsent } from '@demand-iq/consent';
import posthog, { type BeforeSendFn, type CaptureResult } from 'posthog-js';

let initialized = false;

// Errors we expect to hit during normal operation and that are not actionable
// bugs. A 401 ("Unauthenticated") fires on every in-flight query the moment a
// session expires, and QueryClient.ts retries 401s up to 5 times — so a single
// expired session can emit a burst of identical `$exception` events. We drop
// them client-side in `before_send` so they're never transmitted to PostHog,
// keeping exception-ingestion volume (and the bill) down. Extend this list as
// other expected, non-actionable API errors surface.
const IGNORED_EXCEPTION_MESSAGE_PATTERNS = ['HTTP-Code: 401'] as const;

type ExceptionListItem = Readonly<{ type?: string; value?: string }>;

const isIgnoredException = (event: CaptureResult): boolean => {
  if (event.event !== '$exception') {
    return false;
  }

  const exceptionList = event.properties?.$exception_list;

  if (!Array.isArray(exceptionList)) {
    return false;
  }

  return exceptionList.some(
    (item: ExceptionListItem) =>
      typeof item?.value === 'string' &&
      IGNORED_EXCEPTION_MESSAGE_PATTERNS.some((pattern) => item.value?.includes(pattern))
  );
};

const beforeSend: BeforeSendFn = (event) => {
  if (event && isIgnoredException(event)) {
    return null;
  }

  return event;
};

// FE-3 (JOU-196): apply consent to the portal PostHog instance. Analytics consent toggles all
// capture; session_recording consent gates replay. Session replay is prod-only (see loadPostHog's
// disable_session_recording), so we only start/stop it in production and never turn it on elsewhere.
// With consent gating disabled — the default, since portal has no consent signal source yet — every
// category resolves `granted`, so this is a no-op and portal behaves exactly as before. It becomes
// live once a company-level opt-out (BE-2 / FE-6) drives consent. Idempotent.
const applyConsent = (): void => {
  if (!initialized) {
    return;
  }

  if (isConsentGranted('analytics')) {
    // Guard like the journeys apps do: opt_in_capturing() captures a "$opt_in" event by
    // default, so calling it unconditionally would emit one on every page load.
    if (posthog.has_opted_out_capturing()) {
      posthog.opt_in_capturing();
    }
  } else {
    posthog.opt_out_capturing();
  }

  if (import.meta.env.MODE !== 'production') {
    return;
  }

  if (isConsentGranted('session_recording')) {
    posthog.startSessionRecording();
  } else {
    posthog.stopSessionRecording();
  }
};

export const loadPostHog = (): void => {
  const key = import.meta.env.VITE_POSTHOG_KEY;
  const host = import.meta.env.VITE_POSTHOG_HOST;

  if (!key || !host) {
    return;
  }

  const isLocal = import.meta.env.DEV;

  posthog.init(key, {
    api_host: host,
    autocapture: !isLocal,
    capture_pageview: false,
    capture_heatmaps: false,
    capture_exceptions: isLocal
      ? false
      : {
          capture_unhandled_errors: true,
          capture_unhandled_rejections: true,
          capture_console_errors: true,
        },
    enable_recording_console_log: !isLocal,
    // Only record sessions in production. Non-production builds (build:dev,
    // build:stage) were recording into the Dev project for no value — gate on
    // the build mode so they stop. `build:prod` sets MODE='production'.
    disable_session_recording: import.meta.env.MODE !== 'production',
    persistence: 'localStorage+cookie',
    before_send: beforeSend,
    loaded: (ph) => {
      ph.register({
        environment: import.meta.env.MODE,
      });
      // FE-3 (JOU-196): apply consent now and on every change. No-op while gating is off.
      initialized = true;
      applyConsent();
      subscribeConsent(() => applyConsent());
    },
  });

  initialized = true;
};

type IdentifyArgs = Readonly<{
  id: string;
  email?: string;
  companyId?: string;
  companyName?: string;
  role?: string;
  username?: string;
}>;

export const identifyUser = ({ id, email, companyId, companyName, role, username }: IdentifyArgs): void => {
  if (!initialized) {
    return;
  }

  posthog.identify(id, {
    email,
    companyId,
    companyName,
    role,
    username,
  });

  if (companyId) {
    posthog.group('company', companyId, { name: companyName });
  }
};

export const resetUser = (): void => {
  if (!initialized) {
    return;
  }

  posthog.reset();
};

export const capturePageview = (): void => {
  if (!initialized) {
    return;
  }

  // Let posthog-js populate $current_url/$host/$pathname from window.location. The browser URL is
  // already updated by the time the location-change effect fires, so this captures the correct full
  // URL. Passing a path-only $current_url here previously produced malformed URLs in Web Analytics.
  posthog.capture('$pageview');
};

export const captureEvent = (name: string, props?: Record<string, unknown>): void => {
  if (!initialized) {
    return;
  }

  posthog.capture(name, props);
};

export const dismissOpenSurveys = (): void => {
  if (typeof document === 'undefined') {
    return;
  }

  document.querySelectorAll('[id^="PostHogSurvey-"]').forEach((el) => el.remove());
};
